// src/store/designer.js
// Vuex module for the new canvas label designer
// Uses /api/designer endpoints on kdev.datafangst.dk

import apiClient from '../services/api'

const state = {
  designs:         [],
  currentDesign:   null,
  elements:        [],
  loading:         false,
  error:           null,
  selectedElement: null,
  canvasZoom:      1,
  gridEnabled:     true,
  gridSizeMm:      1,
}

const getters = {
  designs:         s => s.designs,
  activeDesigns:   s => s.designs.filter(d => d.isActive),
  currentDesign:   s => s.currentDesign,
  elements:        s => s.elements,
  loading:         s => s.loading,
  selectedElement: s => s.selectedElement,
  canvasZoom:      s => s.canvasZoom,
  gridEnabled:     s => s.gridEnabled,
  gridSizeMm:      s => s.gridSizeMm,
}

const mutations = {
  SET_DESIGNS(state, designs)           { state.designs         = designs },
  SET_CURRENT_DESIGN(state, design)     { state.currentDesign   = design },
  SET_ELEMENTS(state, elements)         { state.elements        = elements },
  SET_LOADING(state, val)               { state.loading         = val },
  SET_ERROR(state, val)                 { state.error           = val },
  SET_SELECTED_ELEMENT(state, el)       { state.selectedElement = el },
  SET_CANVAS_ZOOM(state, val)           { state.canvasZoom      = val },
  SET_GRID_ENABLED(state, val)          { state.gridEnabled     = val },
  SET_GRID_SIZE(state, val)             { state.gridSizeMm      = val },
  UPDATE_ELEMENT(state, el) {
    const idx = state.elements.findIndex(e => e.elementId === el.elementId)
    if (idx !== -1) state.elements.splice(idx, 1, el)
    if (state.selectedElement?.elementId === el.elementId) {
      state.selectedElement = el
    }
  },
  UPDATE_DESIGN_FIELD(state, { key, value }) {
    if (state.currentDesign) state.currentDesign[key] = value
  },
}

const actions = {

  // ── Fetch all designs ──────────────────────────────────
  async fetchDesigns({ commit }) {
    commit('SET_LOADING', true)
    try {
      const { data } = await apiClient.get('/designer')
      commit('SET_DESIGNS', data.designs || [])
    } catch (err) {
      commit('SET_ERROR', err.message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ── Load a single design with elements ─────────────────
  async loadDesign({ commit }, designId) {
    commit('SET_LOADING', true)
    try {
      const { data } = await apiClient.get(`/designer/${designId}`)
      commit('SET_CURRENT_DESIGN', data)
      commit('SET_ELEMENTS', data.elements || [])
      commit('SET_SELECTED_ELEMENT', null)
      return data
    } catch (err) {
      commit('SET_ERROR', err.message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ── Create new design ──────────────────────────────────
  async createDesign({ commit, dispatch }, payload) {
    commit('SET_LOADING', true)
    try {
      const { data } = await apiClient.post('/designer', payload)
      await dispatch('fetchDesigns')
      return data.designId
    } catch (err) {
      commit('SET_ERROR', err.message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ── Update design metadata ─────────────────────────────
  async updateDesign({ commit }, { designId, payload }) {
    try {
      await apiClient.put(`/designer/${designId}`, payload)
      commit('UPDATE_DESIGN_FIELD', { key: 'labelName',   value: payload.labelName   ?? undefined })
      commit('UPDATE_DESIGN_FIELD', { key: 'labelWidth',  value: payload.labelWidth  ?? undefined })
      commit('UPDATE_DESIGN_FIELD', { key: 'labelHeight', value: payload.labelHeight ?? undefined })
      commit('UPDATE_DESIGN_FIELD', { key: 'labelColor',  value: payload.labelColor  ?? undefined })
      commit('UPDATE_DESIGN_FIELD', { key: 'isActive',    value: payload.isActive    ?? undefined })
    } catch (err) {
      commit('SET_ERROR', err.message)
      throw err
    }
  },

  // ── Delete design ──────────────────────────────────────
  async deleteDesign({ dispatch }, designId) {
    await apiClient.delete(`/designer/${designId}`)
    await dispatch('fetchDesigns')
  },

  // ── Copy design ────────────────────────────────────────
  async copyDesign({ dispatch }, designId) {
    const { data } = await apiClient.post(`/designer/${designId}/copy`)
    await dispatch('fetchDesigns')
    return data.designId
  },

  // ── Toggle active ──────────────────────────────────────
  async toggleActive({ dispatch }, { designId, isActive }) {
    await apiClient.put(`/designer/${designId}`, { isActive: isActive ? 1 : 0 })
    await dispatch('fetchDesigns')
  },

  // ── Save elements ──────────────────────────────────────
  async saveElements({ state, commit }) {
    if (!state.currentDesign) return
    commit('SET_LOADING', true)
    try {
      await apiClient.post(
        `/designer/${state.currentDesign.designId}/elements`,
        { elements: state.elements }
      )
    } catch (err) {
      commit('SET_ERROR', err.message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // ── Import from old KitchenPrint label system ──────────
  async importFromLegacy({ dispatch }, legacyLabel) {
    // Map old label fields → new designer_labels + designer_elements
    const design = {
      labelName:   legacyLabel.labelName,
      labelWidth:  legacyLabel.labelWidth,
      labelHeight: legacyLabel.labelHeight,
      labelColor:  legacyLabel.labelColor || '#448AFF',
      labelType:   legacyLabel.labelType  || 'standard',
    }

    const designId = await dispatch('createDesign', design)

    // Map old fields → new elements
    const elements = []
    let order = 0

    // Old system has itemName as title field — create a text element
    if (legacyLabel.fontSizeName) {
      elements.push({
        fieldType:    'database',
        fieldLabel:   'Produktnavn',
        databaseField: 'product_name',
        posX:         parseFloat(legacyLabel.fontNameX) || 2,
        posY:         parseFloat(legacyLabel.fontNameY) || 2,
        elementWidth: legacyLabel.labelWidth - (parseFloat(legacyLabel.fontNameX) || 2) - 1,
        elementHeight: parseFloat(legacyLabel.fontSizeName) * 0.353 * 1.4,
        fontSize:     parseFloat(legacyLabel.fontSizeName) * 0.353,
        fontBold:     1,
        textAlign:    'left',
        zIndex:       order++,
        fieldOrder:   order,
      })
    }

    // Map existing fields
    for (const f of legacyLabel.fields || []) {
      const el = _mapLegacyField(f, order)
      if (el) {
        el.fieldOrder = ++order
        el.zIndex     = order
        elements.push(el)
      }
    }

    // Logo field
    if (legacyLabel.logoData) {
      elements.push({
        fieldType:   'image',
        fieldLabel:  'Logo',
        imageData:   legacyLabel.logoData,
        posX:        2,
        posY:        2,
        elementWidth: legacyLabel.logoData.widthMm  || 15,
        elementHeight: legacyLabel.logoData.heightMm || 5,
        zIndex:      order++,
        fieldOrder:  order,
      })
    }

    if (elements.length) {
      await apiClient.post(`/designer/${designId}/elements`, { elements })
    }

    return designId
  },
}

// ── Field mapper helper ──────────────────────────────────
function _mapLegacyField(f, order) {
  const base = {
    fieldLabel:   f.fieldLabel || '',
    posX:         parseFloat(f.labelX)  || 0,
    posY:         parseFloat(f.labelY)  || 0,
    elementWidth: 40,
    elementHeight: parseFloat(f.fontSize || 9) * 0.353 * 1.4,
    fontSize:     parseFloat(f.fontSize || 9) * 0.353,
    fontBold:     f.fontBold  ? 1 : 0,
    fontItalic:   f.fontItalic ? 1 : 0,
    textAlign:    f.textAlign || 'left',
    rotation:     String(f.rotation || '0'),
  }

  switch (f.fieldType) {
    case 'date':
      return { ...base, fieldType: 'date', databaseField: 'print_date' }

    case 'date_plus': {
      const fieldMap = {
        thawing:      'expiry_date',
        refrigerated: 'use_by_date',
        expiry:       'expiry_date',
      }
      return {
        ...base,
        fieldType:    'date',
        databaseField: fieldMap[f.daysOffsetField] || 'expiry_date',
      }
    }

    case 'database': {
      const dbMap = {
        itemName:    'product_name',
        fridge:      'description',
        three_hours: 'description',
        barcode:     'ean_code',
        description: 'description',
        alias:       'product_name',
      }
      return {
        ...base,
        fieldType:    'database',
        databaseField: dbMap[f.databaseField] || 'product_name',
      }
    }

    case 'static':
      return { ...base, fieldType: 'static' }

    case 'barcode':
      return {
        ...base,
        fieldType:     'barcode',
        barcodeType:   'code128',
        barcodeWidth:  parseFloat(f.barcodeWidth)  || 30,
        barcodeHeight: parseFloat(f.barcodeHeight) || 10,
        barcodeDataField: f.barcodeDataField === 'barcode' ? 'ean_code' : 'product_number',
        elementWidth:  parseFloat(f.barcodeWidth)  || 30,
        elementHeight: parseFloat(f.barcodeHeight) || 10,
      }

    case 'qrcode':
      return {
        ...base,
        fieldType:   'qrcode',
        qrSize:      parseFloat(f.qrSize) || 15,
        qrDataField: 'ean_code',
        elementWidth:  parseFloat(f.qrSize) || 15,
        elementHeight: parseFloat(f.qrSize) || 15,
      }

    case 'allergens_text':
      return { ...base, fieldType: 'database', databaseField: 'allergens' }

    case 'divider':
      return {
        ...base,
        fieldType:          'divider',
        dividerOrientation: 'horizontal',
        dividerThickness:   parseFloat(f.lineThickness) || 0.5,
        elementWidth:       parseFloat(f.lineWidth) || 30,
        elementHeight:      parseFloat(f.lineThickness) || 0.5,
      }

    case 'prompt':
      return { ...base, fieldType: 'static', fieldLabel: `[${f.fieldLabel}]` }

    default:
      return null
  }
}

export default { namespaced: true, state, getters, mutations, actions }
